import CheckinsRepository, {
  ReassignGuestArgs,
} from '@/api/repositories/checkins'
import saveStateToStorage from '@/utils/saveStateToStorage'
import getSavedState from '@/utils/getSavedState'
import { Checkin, Guest } from '@/types/api/types'
import { PatchCheckinGuestArgs } from '@/api/repositories/checkins'
import { getters as bookingGetters, state as bookingState } from './bookings'

export type CurrentStep = {
  number: number
  totalStepCount: number
  messageKey: string
  notFound?: boolean
}

export const state = {
  checkin: getSavedState('checkin'),
  lastName: null,
}

export const getters = {
  checkin(state): Checkin {
    return state.checkin
  },
  guest(state): Guest {
    return state.checkin?.guest
  },
  guestIsReturning(_state, getters) {
    return getters.guest?.returning
  },
  guestIsJapanResident(_state, getters) {
    return getters.guest?.country === 'JP'
  },
  guestIsJapanCitizenOrResident(_state, getters) {
    return (
      getters.guest?.country === 'JP' || getters.guest?.nationality === 'JP'
    )
  },
  guestIsPrimary(state) {
    return !!state.checkin?.primary
  },
  guestExists(_state, getters) {
    return !!getters.guest?.id
  },
  guestEmail(_state, getters) {
    return getters.guest?.email
  },
  guestIsCheckedIn(state) {
    return !!state.checkin?.completedAt
  },
  guestIsPreCheckedIn(_state, getters) {
    return getters.guest?.idPhotoKey && !getters.guestIsCheckedIn
  },
  checkinSteps(state, _getters, _rootState, rootGetters): { name: string; messageKey: string }[] {
    const booking = bookingGetters.booking(bookingState)
    // Since ENG-2921 the guest reaches GuestInfo before a check-in exists, so keying the step
    // list off the check-in alone blanked the progress header on that screen. The booking is what
    // actually says whether we are inside a check-in flow; outside one there is still no list.
    if (!state.checkin && !booking) return []

    const bookingHasOthers =
      (booking?.numAdult || 1) > 1 || (booking?.numChild || 0) > 0
    // No check-in yet means the Email screen told us via the route which kind of guest this is.
    const isPrimary =
      state.checkin?.primary ?? _rootState.route?.query?.primary === 'true'
    const isFullCheckinFlow = rootGetters['properties/isFullCheckinFlow']
    const checkInIsUnderEighteen = () => {
      const dob = state.checkin?.guest?.dateOfBirth
      if (!dob) return false

      const ciDate = booking?.checkinDate
      if (!ciDate) return false

      const dobDate = new Date(dob)
      const checkinDate = new Date(ciDate)

      // Doing "+ 1" day here means the guest will be considered to have turned the new age ON their birthday instead of one day after
      const guestDob = new Date(dobDate.setDate(dobDate.getDate() + 1))
      const timeDifference = new Date(checkinDate.getTime() - guestDob.getTime())

      const guestAgeInYearsAtCheckin = Math.abs(
        timeDifference.getUTCFullYear() - 1970
      )

      return guestAgeInYearsAtCheckin < 18
    }

    const mugshotIsSkippable = rootGetters['properties/propertyIsMannedRightNow']

    const checkInFlows = {
      atProperty: [
        { name: 'GuestInfo', messageKey: 'pages.checkin.steps.guestInfo' },
        { name: 'ContactInfo', messageKey: 'pages.checkin.steps.contactInfo' },
        ...(checkInIsUnderEighteen() ? [] : [{ name: 'TravelInfo', messageKey: 'pages.checkin.steps.travelInfo' }]),
        { name: 'IdInfo', messageKey: 'pages.checkin.steps.id' },
        {
          name: mugshotIsSkippable
            ? 'MugshotCaptureSkippable'
            : 'MugshotCapture',
          messageKey: 'pages.checkin.steps.mugshot',
        },
        ...(checkInIsUnderEighteen() ? [] : [{
          name: 'CheckinAgreement',
          messageKey: 'pages.checkin.steps.agreement',
        }]),
      ],
      notAtProperty: [
        { name: 'GuestInfo', messageKey: 'pages.checkin.steps.guestInfo' },
        { name: 'ContactInfo', messageKey: 'pages.checkin.steps.contactInfo' },
        ...(checkInIsUnderEighteen() ? [] : [{ name: 'TravelInfo', messageKey: 'pages.checkin.steps.travelInfo' }]),
        { name: 'IdInfo', messageKey: 'pages.checkin.steps.id' },
        ...(isPrimary && bookingHasOthers
          ? [
            {
              name: 'OtherGuests',
              messageKey: 'pages.checkin.steps.otherGuests',
            },
          ]
          : []),
      ],
    }

    return isFullCheckinFlow ? checkInFlows.atProperty : checkInFlows.notAtProperty
  },
  currentStep(_, getters, rootState): CurrentStep {
    const defaultCheckinStep = {
      number: 0,
      messageKey: 'general.start',
      notFound: true,
    }

    const steps = getters.checkinSteps

    return {
      totalStepCount: steps.length,
      ...steps.reduce((acc, { name, messageKey }, idx) => {
        const foundCurrentRoute = name === rootState.route?.name
        return foundCurrentRoute
          ? {
            number: idx,
            messageKey,
          }
          : acc
      }, defaultCheckinStep),
    }
  },
  lastName(state) {
    return state.lastName
  },
}

export const mutations = {
  SET_CHECKIN(state, checkin) {
    state.checkin = checkin
    saveStateToStorage<Checkin>('checkin', state.checkin)
  },

  RESET_CHECKIN(state) {
    state.checkin = null
    saveStateToStorage<Checkin>('checkin', state.checkin)
  },
}

export const actions = {
  async setCheckin({ commit }, checkin) {
    commit('SET_CHECKIN', checkin)
  },
  // Returns [checkin, null] on success, [null, error] on failure, and [null, null] when
  // deferCreation was asked for and the server does not know this email — nothing was created,
  // and the caller carries the email forward instead. ENG-2921.
  async createCheckin(
    { commit, dispatch, rootGetters },
    { email, bookingNo, primary, inviteUuid, dateOfBirth, deferCreation = false }
  ) {
    try {
      const response = await CheckinsRepository.postCheckin({
        email,
        bookingNo,
        propCode: rootGetters['properties/code'],
        primary,
        inviteUuid,
        dateOfBirth,
        deferCreation,
      })
      // 204, no body: there is no check-in to store, and storing an empty one would leave the
      // rest of the flow believing a record exists.
      if (response.status === 204 || !response.data) return [null, null]

      const newCheckin = response.data as Checkin
      dispatch('bookings/addCheckin', { checkin: newCheckin }, { root: true })
      commit('SET_CHECKIN', newCheckin)
      return [newCheckin, null]
    } catch (e) {
      return [null, e]
    }
  },
  async updateCheckin({ commit, dispatch, rootGetters }, { checkin, preCheckin = false }) {
    const response = await CheckinsRepository.patchCheckin({
      checkin,
      propCode: rootGetters['properties/code'],
      preCheckin,
    })
    const updatedCheckin = response.data
    dispatch('bookings/addCheckin', { checkin: updatedCheckin }, { root: true })
    commit('SET_CHECKIN', updatedCheckin)
    return updatedCheckin
  },
  async updateCheckinGuest(
    { commit, rootGetters },
    { checkinId, guest }: Omit<PatchCheckinGuestArgs, 'propCode'>
  ): Promise<Checkin> {
    const response = await CheckinsRepository.patchGuest({
      checkinId,
      guest,
      propCode: rootGetters['properties/code'],
    })
    const updatedCheckin = response.data
    commit('SET_CHECKIN', updatedCheckin)
    return updatedCheckin
  },
  async reassignGuest(
    { state, commit },
    { email, dateOfBirth }: ReassignGuestArgs
  ): Promise<
    [Checkin, null] | [null, { type: string; message: string; metadata?: any }]
  > {
    try {
      const checkinId = getters.checkin(state).id
      const response = await CheckinsRepository.reassignGuest({
        checkinId,
        email,
        dateOfBirth,
      })
      const updatedCheckin = response.data
      commit('SET_CHECKIN', updatedCheckin)
      return [updatedCheckin, null]
    } catch (e) {
      return [null, e]
    }
  },
  resetCheckin({ commit }) {
    commit('RESET_CHECKIN')
  },
}
